import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';

import { EntityVerificationService } from '../../core/ops/entity-verification.service';
import { ENTITY_TYPE_LABELS, ENTITY_TYPE_OPTIONS, EntityType } from '../../core/entity/entity.types';
import { BankAccount, CipcStatus, EntityVerificationPayload, EntityVerificationState } from '../../core/ops/entity-verification.types';
import { KsTaxPinOps } from '../../core/kickstart/kickstart.types';
import { openBlob } from '../blob-file';
import { DashPipe } from '../pipes/dash.pipe';
import { GENERIC_ERROR } from '../../core/http/read-error';

/**
 * BRE-200 §2.4 — the canonical entity-level verification record (FLOW-160).
 *
 * **One component for both lanes, deliberately.** §2.4 makes these checks
 * "stored once in a single entity-level verification record, referenced by both
 * lanes"; a KS-shaped copy and a DF-shaped copy of this panel would reintroduce
 * at the UI layer the duplication the data model just removed.
 *
 * Ops-only (N4) — never import into a client-facing component. BRE-200 §2.1
 * assigns all four checks to Ops, and an applicant must not see, let alone set,
 * the checks that gate its own application.
 *
 * Authority is standard Ops, Analyst-inclusive: BRE-200 §8 reserves elevated
 * authority for *waivers*, not for recording a condition genuinely met.
 */
@Component({
  selector: 'app-entity-verification-panel',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    DatePipe,
    DashPipe,
  ],
  templateUrl: './entity-verification-panel.html',
  styleUrl: './entity-verification-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityVerificationPanelComponent implements OnInit {
  private readonly verificationService = inject(EntityVerificationService);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /** short_id of the business whose canonical record this panel edits. */
  readonly entityShortId = input.required<string>();
  /**
   * FLOW-352 (DOC-83 §3) — the tax compliance PIN the business sent, for the
   * tax line: Kickstart passes it, Debtors Finance does not (`DF-OB-08` parked),
   * and the line is then as before. The result is still recorded with the SARS
   * control below — nothing new is added there.
   */
  readonly taxPin = input<{ owed: boolean; pins: readonly KsTaxPinOps[] } | null>(null);
  /** A verification was recorded — the page re-reads what depends on it (FLOW-400). */
  readonly recorded = output<void>();

  protected readonly state = signal<EntityVerificationState | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly serverError = signal<string | null>(null);

  /**
   * `null` on every control means "not addressed" — the payload is built from
   * changed values only, so an untouched control is omitted rather than sent as
   * `false`. Sending the whole form would restamp checks nobody re-performed.
   */
  protected readonly form = this.fb.group({
    cipc_status: [null as CipcStatus | null],
    kyc_verified: [null as boolean | null],
    sars_verified: [null as boolean | null],
    bank_account_ownership_verified: [null as boolean | null],
    /**
     * Which account the KS-OB-03 record names. Validated in `save()` rather
     * than by a `Validators.required`, because it is required *conditionally* —
     * only when the checkbox above is ticked. A static required-validator would
     * block recording SARS on a business with no bank account on file.
     */
    bank_account: [null as string | null],
    /**
     * The legal form on the CIPC record (FLOW-342). Pre-filled with the type as
     * recorded, so it reaches the payload only alongside `cipc_status` — the
     * two are read off the same record — and corrects the type only where it
     * differs, with a reason.
     */
    legal_form: [null as EntityType | null],
    reg_no_as_printed: [''],
    type_correction_reason: [''],
  });

  protected readonly entityTypes = ENTITY_TYPE_OPTIONS;
  protected readonly typeLabels = ENTITY_TYPE_LABELS;

  // -- Bank accounts (BRE-SCH-001 3.23, FLOW-171) --------------------------
  protected readonly accounts = signal<BankAccount[]>([]);
  protected readonly accountsError = signal(false);
  protected readonly showCapture = signal(false);
  protected readonly isCapturing = signal(false);
  protected readonly captureError = signal<string | null>(null);

  protected readonly captureForm = this.fb.group({
    bank_name: ['', Validators.required],
    branch_code: ['', Validators.required],
    account_number: ['', Validators.required],
    account_holder_name: ['', Validators.required],
    /**
     * Tri-state, and `null` is the default on purpose: `false` is the
     * substantive claim *"a third party holds this account"*, not the absence
     * of an answer. Whether a third-party debit account is permitted for
     * Kickstart at all is an open policy question with the Product Owner and
     * Credit Policy, so the UI must be able to record "not asked".
     */
    holder_is_borrower: [null as boolean | null],
  });

  // -- CIPC registration date (FLOW-356) ------------------------------------
  protected readonly cipcForm = this.fb.group({
    registration_date: ['', Validators.required],
    correction_reason: [''],
  });
  protected readonly cipcFile = signal<File | null>(null);
  protected readonly showCipcCorrection = signal(false);
  protected readonly isRecordingCipc = signal(false);
  protected readonly cipcError = signal<string | null>(null);

  protected readonly cipcOptions: { value: CipcStatus; label: string }[] = [
    { value: 'IN_BUSINESS', label: 'In Business' },
    { value: 'DEREGISTRATION_IN_PROGRESS', label: 'Deregistration in Progress' },
    { value: 'DEREGISTERED', label: 'Deregistered' },
    { value: 'IN_BUSINESS_RESCUE', label: 'In Business Rescue' },
  ];

  protected readonly holderOptions: { value: boolean; label: string }[] = [
    { value: true, label: 'The business' },
    { value: false, label: 'A third party' },
  ];

  ngOnInit(): void {
    this.load();
    this.loadAccounts();
  }

  protected retryLoad(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.cdr.markForCheck();
    this.load();
    this.loadAccounts();
  }

  private loadAccounts(): void {
    this.accountsError.set(false);
    this.verificationService
      .opsListBankAccounts(this.entityShortId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ bank_accounts }) => {
          this.accounts.set(bank_accounts);
          this.cdr.markForCheck();
        },
        error: () => {
          this.accountsError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected retryAccounts(): void {
    this.loadAccounts();
  }

  protected toggleCapture(): void {
    this.showCapture.update((open) => !open);
    // Reset on close, not just clear — otherwise the required-validators fire
    // on controls that look untouched next time it opens (CONFLICTS.md S9-4).
    this.captureForm.reset();
    this.captureError.set(null);
  }

  protected captureAccount(): void {
    if (this.captureForm.invalid) {
      this.captureForm.markAllAsTouched();
      return;
    }

    this.isCapturing.set(true);
    this.captureError.set(null);
    this.cdr.markForCheck();

    const v = this.captureForm.getRawValue();
    this.verificationService
      .opsCreateBankAccount(this.entityShortId(), {
        bank_name: v.bank_name!,
        branch_code: v.branch_code!,
        account_number: v.account_number!,
        account_holder_name: v.account_holder_name!,
        // Omitted when null — the backend records "not captured" rather than
        // being told the holder is a third party.
        ...(v.holder_is_borrower !== null && { holder_is_borrower: v.holder_is_borrower }),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (account) => {
          this.accounts.update((list) => [...list, account]);
          this.isCapturing.set(false);
          this.showCapture.set(false);
          this.captureForm.reset();
          // Preselect it: capturing an account is almost always the first half
          // of verifying it, and making Ops re-find it in the list is friction
          // with no safety value — the verification is still a separate act.
          this.form.controls.bank_account.setValue(account.short_id);
          this.form.controls.bank_account.markAsDirty();
          this.snackBar.open('Bank account added.', 'Dismiss', { duration: 5000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isCapturing.set(false);
          const status = (err as { status?: number })?.status;
          this.captureError.set(
            status === 409
              ? 'This business already has that bank account.'
              : "Couldn't add the bank account. Please try again.",
          );
          this.cdr.markForCheck();
        },
      });
  }

  protected pickCipcFile(event: Event): void {
    this.cipcFile.set((event.target as HTMLInputElement).files?.[0] ?? null);
  }

  protected toggleCipcCorrection(): void {
    this.showCipcCorrection.update((open) => !open);
    this.cipcForm.reset({ registration_date: '', correction_reason: '' });
    this.cipcFile.set(null);
    this.cipcError.set(null);
  }

  /**
   * Captures the date, or — when one is on file — corrects it with a reason.
   * The backend refuses a second capture; this form only ever offers the
   * correction once a date exists.
   */
  protected recordCipcDate(): void {
    const correcting = !!this.state()?.cipc_registration_date;
    const { registration_date, correction_reason } = this.cipcForm.getRawValue();
    const file = this.cipcFile();
    const reason = (correction_reason ?? '').trim();
    const missing = !registration_date
      ? 'Enter the registration date shown on the CIPC document.'
      : !file
        ? 'Attach the CIPC document the date was read from.'
        : correcting && !reason
          ? 'Give the reason for the correction.'
          : null;
    if (missing) {
      this.cipcError.set(missing);
      this.cdr.markForCheck();
      return;
    }

    this.isRecordingCipc.set(true);
    this.cipcError.set(null);
    this.cdr.markForCheck();
    this.verificationService
      .opsRecordCipcRegistrationDate(this.entityShortId(), {
        registration_date: registration_date!,
        file: file!,
        ...(correcting && { correction_reason: reason }),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.state.set(state);
          this.isRecordingCipc.set(false);
          this.showCipcCorrection.set(false);
          this.cipcForm.reset({ registration_date: '', correction_reason: '' });
          this.cipcFile.set(null);
          this.snackBar.open('CIPC registration date recorded.', 'Dismiss', { duration: 5000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isRecordingCipc.set(false);
          const detail = (err as { error?: { detail?: unknown } })?.error?.detail;
          this.cipcError.set(
            typeof detail === 'string'
              ? detail
              : (firstFieldError(detail) ?? "Couldn't record the date. Please try again."),
          );
          this.cdr.markForCheck();
        },
      });
  }

  protected openCipcFile(fileId: number): void {
    this.verificationService
      .opsCipcRegistrationFile(this.entityShortId(), fileId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => openBlob(blob),
        error: () =>
          this.snackBar.open('The document could not be fetched. Please try again.', 'Dismiss', {
            duration: 5000,
          }),
      });
  }

  private load(): void {
    this.verificationService
      .opsGetVerification(this.entityShortId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.state.set(state);
          this.prefillLegalForm(state);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  private prefillLegalForm(state: EntityVerificationState): void {
    this.form.controls.legal_form.reset(state.type);
  }

  /** The selected legal form differs from the type as recorded. */
  protected typeDiffers(): boolean {
    const selected = this.form.controls.legal_form.value;
    return selected !== null && selected !== this.state()?.type;
  }

  /**
   * The suffix hint, shown only where it points somewhere other than the
   * current selection. Never applied — the CIPC record decides.
   */
  protected suggestedOther(): EntityType | null {
    const suggested = this.state()?.suggested_type ?? null;
    return suggested !== null && suggested !== this.form.controls.legal_form.value
      ? suggested
      : null;
  }

  /** Only dirty controls with a non-null value reach the payload. */
  private buildPayload(): EntityVerificationPayload {
    const payload: EntityVerificationPayload = {};
    const v = this.form.getRawValue();
    if (this.form.controls.cipc_status.dirty && v.cipc_status !== null) {
      payload.cipc_status = v.cipc_status;
      if (v.legal_form !== null) {
        payload.cipc_legal_form = v.legal_form;
        if (this.typeDiffers()) {
          payload.type_correction_reason = (v.type_correction_reason ?? '').trim();
          const printed = (v.reg_no_as_printed ?? '').trim();
          if (printed) payload.cipc_reg_no_as_printed = printed;
        }
      }
    }
    if (this.form.controls.kyc_verified.dirty && v.kyc_verified !== null) {
      payload.kyc_verified = v.kyc_verified;
    }
    if (this.form.controls.sars_verified.dirty && v.sars_verified !== null) {
      payload.sars_verified = v.sars_verified;
    }
    if (
      this.form.controls.bank_account_ownership_verified.dirty &&
      v.bank_account_ownership_verified !== null
    ) {
      payload.bank_account_ownership_verified = v.bank_account_ownership_verified;
      // Only a TRUE carries an account. A `false` says "Ops determined this is
      // not verified", which is about the business and names no account —
      // sending one would assert a subject for a negative finding.
      if (v.bank_account_ownership_verified && v.bank_account) {
        payload.bank_account = v.bank_account;
      }
    }
    return payload;
  }

  protected save(): void {
    // FLOW-342: a correction is made at the CIPC check, with a reason. Both
    // caught here to save a round-trip that can only fail; the backend holds
    // the rule.
    if (this.typeDiffers()) {
      if (!this.form.controls.cipc_status.dirty || this.form.controls.cipc_status.value === null) {
        this.serverError.set(
          'Record the CIPC status you checked — the legal form is recorded with it.',
        );
        this.cdr.markForCheck();
        return;
      }
      if (!(this.form.controls.type_correction_reason.value ?? '').trim()) {
        this.serverError.set('Give the reason for correcting the legal form.');
        this.cdr.markForCheck();
        return;
      }
    }

    const payload = this.buildPayload();
    if (Object.keys(payload).length === 0) {
      this.serverError.set('Change at least one check before recording.');
      this.cdr.markForCheck();
      return;
    }

    // Caught here as well as by the backend, and the two are not redundant:
    // this one keeps Ops out of a round-trip that can only fail, the backend's
    // is the rule itself — "a verification that cannot name its subject is not
    // a verification" (BRE-SCH-001 v1.16 §3).
    if (payload.bank_account_ownership_verified === true && !payload.bank_account) {
      this.serverError.set(
        'Choose which bank account you verified. A verification has to name the account it confirms.',
      );
      this.cdr.markForCheck();
      return;
    }

    this.isSaving.set(true);
    this.serverError.set(null);
    this.cdr.markForCheck();

    this.verificationService
      .opsRecordVerification(this.entityShortId(), payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.state.set(state);
          this.isSaving.set(false);
          this.form.reset();
          this.prefillLegalForm(state);
          // The account's own verified flag has moved too — v1.16 §3 property 4
          // keeps a record per account, and the list shows it.
          this.loadAccounts();
          this.recorded.emit();
          this.snackBar.open('Verification recorded.', 'Dismiss', { duration: 5000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isSaving.set(false);
          const detail = (err as { error?: { detail?: unknown } })?.error?.detail;
          this.serverError.set(
            typeof detail === 'string' ? detail : (firstFieldError(detail) ?? GENERIC_ERROR),
          );
          this.cdr.markForCheck();
        },
      });
  }
}

/** The first message of a DRF field-error object, e.g. the legal-form mismatch. */
function firstFieldError(detail: unknown): string | null {
  if (!detail || typeof detail !== 'object') return null;
  for (const messages of Object.values(detail as Record<string, unknown>)) {
    if (Array.isArray(messages) && typeof messages[0] === 'string') return messages[0];
  }
  return null;
}
